import Heading from "atoms/typography/heading";
import AlertLevels from "constants/alert-levels";
import { siteMap } from "internal-sitemap";
import AlertNotice from "molecules/banners/alert-notice";
import FullScreenTransition from "organisms/full-screen-transition/full-screen-transition";
import { ReadonlyPageBlock } from "organisms/page-blocks/readonly-page-block/readonly-page-block";
import PrintablePageFooter from "pages/notebooks/printing/printable-page-footer";
import usePrintablePage from "pages/notebooks/printing/use-printable-page";
import React, { useEffect } from "react";
import { Redirect } from "react-router";
import Printable from "templates/printable";
import { SharedPageContext } from "utilities/contexts/shared-page/shared-page-context";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import usePageBlocks from "utilities/hooks/domain/notebooks/pages/use-page-blocks";
import { useSetAtom } from "jotai";
import { imageLoadingStrategyAtom } from "utilities/atoms/image-loading-strategy-atom";
import { t } from "utilities/localization-utils";
import RemoteImage from "atoms/images/remote-image";

interface PrintablePagePageProps {}

const CSS_CLASS_NAME = "c-printable-page-page";

const PrintablePagePage = (_: PrintablePagePageProps) => {
    const {
        currentPage,
        isLoading,
        isErrored,
        isPublicPage,
        publicSharedState,
    } = usePrintablePage();
    const { globalState } = useGlobalState();
    const { populatedBlocks } = usePageBlocks({
        isPublic: isPublicPage,
        isPrinting: true,
        pageGuid: publicSharedState.pageGuid,
        recipientGuid: publicSharedState.recipientGuid,
    });
    const setImageLoadingStrategy = useSetAtom(imageLoadingStrategyAtom);

    useEffect(() => {
        setImageLoadingStrategy("eager");
    }, [setImageLoadingStrategy]);

    // If a user is no authenticated but is trying to print a non-Public page, redirect to home
    if (!isPublicPage && !globalState.isAuthenticated()) {
        return <Redirect to={siteMap.home} />;
    }

    return (
        <SharedPageContext.Provider value={publicSharedState}>
            <Printable
                cssClassName={CSS_CLASS_NAME}
                footer={
                    <PrintablePageFooter
                        ownerFullName={currentPage?.ownerFullName}
                        ownerEmail={currentPage?.ownerEmail}
                    />
                }>
                {isLoading ? (
                    <FullScreenTransition transitionText={""} />
                ) : isErrored ? (
                    <AlertNotice
                        level={AlertLevels.Error}
                        title={t("errors-actionResource", {
                            action: t("loading"),
                            resource: t("page"),
                        })}
                    />
                ) : (
                    <>
                        <header className={`${CSS_CLASS_NAME}__header`}>
                            <Heading
                                cssClassName={`${CSS_CLASS_NAME}__heading`}>
                                {currentPage?.name}
                            </Heading>
                            <RemoteImage
                                altText={currentPage?.customLogoAltText}
                                fileId={currentPage?.customLogoFileId}
                                cssClassName={`${CSS_CLASS_NAME}__customLogo`}
                            />
                        </header>
                        <section className={`${CSS_CLASS_NAME}__blockList`}>
                            {populatedBlocks.map((block) => (
                                <ReadonlyPageBlock
                                    block={block}
                                    key={block.id}
                                    printable
                                />
                            ))}
                        </section>
                    </>
                )}
            </Printable>
        </SharedPageContext.Provider>
    );
};

export default PrintablePagePage;
